import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './TutorRequests.css';

// ── API Base URL ───────────────────────────────────────────
const API_BASE = 'https://mykaddle-api.onrender.com';

const TutorPrivateRequests = () => {
  const navigate = useNavigate();

  // ── STATE ──
  const [pendingRequests, setPendingRequests] = useState([]);
  const [acceptedRequests, setAcceptedRequests] = useState([]);
  const [finishedRequests, setFinishedRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal states
  const [acceptModalOpen, setAcceptModalOpen] = useState(false);
  const [declineModalOpen, setDeclineModalOpen] = useState(false);
  const [currentRequest, setCurrentRequest] = useState(null);

  // Form states
  const [groupName, setGroupName] = useState('');
  const [rate, setRate] = useState('');
  const [declineReason, setDeclineReason] = useState('');

  // ── FETCH REQUESTS ON MOUNT ──
  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      setError(null);

      const headers = {
        Authorization: `Bearer ${localStorage.getItem('token')}`
      };

      const pendingRes = await axios.get(`${API_BASE}/api/tutor/requests/pending`, { headers });
      const historyRes = await axios.get(`${API_BASE}/api/tutor/requests/history?type=tutor`, { headers });

      setPendingRequests(pendingRes.data.requests || []);

      const requests = historyRes.data.requests || [];
      const accepted = requests.filter(r => r.status === 'accepted' && !r.isFinished);
      const finished = requests.filter(r => r.isFinished);

      setAcceptedRequests(accepted);
      setFinishedRequests(finished);
    } catch (err) {
      console.error('Error fetching requests:', err);
      setError(err.response?.data?.error || 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  // ── ACCEPT REQUEST ──
  const handleAcceptClick = (request) => {
    setCurrentRequest(request);
    setGroupName('');
    if (request.budget) {
      const budgetMap = {
        'R0-R50': 25, 'R50-R100': 75, 'R100-R150': 125, 'R150-R200': 175,
        'R200-R250': 225, 'R250-R300': 275, 'R300-R400': 350, 'R400-R500': 450,
        'R500-R750': 625, 'R750-R1000': 875, '+R1000': 1000
      };
      setRate(budgetMap[request.budget]?.toString() || '');
    } else {
      setRate('');
    }
    setAcceptModalOpen(true);
  };

  const confirmAccept = async () => {
    if (!groupName.trim()) { alert('Please enter a group name'); return; }
    if (!rate || parseFloat(rate) <= 0) { alert('Please enter a valid rate'); return; }

    try {
      await axios.post(
        `${API_BASE}/api/tutor/requests/${currentRequest._id}/accept`,
        { groupName: groupName.trim(), rate: parseFloat(rate) },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      alert('Request accepted! Group created successfully.');
      setAcceptModalOpen(false);
      fetchRequests();
    } catch (err) {
      console.error('Error accepting request:', err);
      alert(err.response?.data?.error || 'Failed to accept request');
    }
  };

  // ── DECLINE REQUEST ──
  const handleDeclineClick = (request) => {
    setCurrentRequest(request);
    setDeclineReason('');
    setDeclineModalOpen(true);
  };

  const confirmDecline = async () => {
    try {
      await axios.post(
        `${API_BASE}/api/tutor/requests/${currentRequest._id}/reject`,
        { reason: declineReason.trim() || undefined },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      alert('Request declined. The student will be notified.');
      setDeclineModalOpen(false);
      fetchRequests();
    } catch (err) {
      console.error('Error declining request:', err);
      alert(err.response?.data?.error || 'Failed to decline request');
    }
  };

  // ── OPEN CHAT ── (fetches request history to find the matching groupSubjectId)
  const handleOpenChat = async (request) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      console.log('🔍 Opening chat for request:', { requestId: request._id, counterpartyName: request.counterpartyName });

      // Fetch request history to find the accepted group's groupSubjectId
      const res = await axios.get(`${API_BASE}/api/tutor/requests/history?type=tutor`, { headers });
      const requests = res.data.requests || [];

      console.log('📋 History requests:', requests.map(r => ({
        _id: r._id,
        groupSubjectId: r.groupSubjectId,
        status: r.status,
        counterpartyName: r.counterpartyName
      })));

      // Find the matching accepted request
      const matchingRequest = requests.find(r => r._id === request._id);

      if (matchingRequest?.groupSubjectId) {
        console.log('✅ Found matching request with groupSubjectId:', matchingRequest.groupSubjectId);
        navigate(`/request-portal/${matchingRequest.groupSubjectId}`);
      } else {
        // No groupSubjectId means this request was likely accepted before
        // groupSubjectId was persisted on the request document, or the
        // group hasn't been fully set up yet. Falling back to request._id
        // will 404 in RequestPortal — surface that clearly instead of
        // silently navigating into a broken portal.
        console.warn('⚠️ No groupSubjectId found for this accepted request. It may need backfilling.', request._id);
        alert("This chat isn't available yet — it looks like the group wasn't fully set up. Please contact support.");
      }
    } catch (err) {
      console.error('Failed to find group:', err);
      alert('Could not open this chat right now. Please try again or contact support.');
    }
  };

  // ── FORMAT DATE ──
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // ── FORMAT DATE RANGE ──
  const formatDateRange = (startDate, endDate) => {
    if (!startDate || !endDate) return '';
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const weeks = Math.ceil(diffDays / 7);
    return `${start.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })} - ${end.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })}, ${weeks} week${weeks > 1 ? 's' : ''}`;
  };

  // ── RENDER ──
  if (loading) {
    return (
      <div className="ttrq-dashboard-container">
        <div className="ttrq-almighty-wrapper">
          <div className="ttrq-empty-state-container">
            <p className="ttrq-empty-state-text">Loading requests...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ttrq-dashboard-container">
        <div className="ttrq-almighty-wrapper">
          <div className="ttrq-empty-state-container">
            <p className="ttrq-empty-state-text" style={{ color: '#d64045' }}>{error}</p>
            <button className="ttrq-req-acc" onClick={fetchRequests} style={{ marginTop: '0.5rem' }}>Retry</button>
          </div>
        </div>
      </div>
    );
  }

  const allRequests = [...pendingRequests, ...acceptedRequests, ...finishedRequests];
  const hasRequests = allRequests.length > 0;

  return (
    <div className="ttrq-dashboard-container">
      <div className="ttrq-almighty-wrapper">
        {/* Top Bar */}
        <div className="ttrq-top-bar">
          <div className="ttrq-top-left-header">
            <div className="ttrq-header-row">
              <button className="ttrq-back-icon-btn" aria-label="Go back" onClick={() => navigate(-1)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="0.5em" height="1em" viewBox="0 0 12 24">
                  <path d="M0 0h12v24H0z" fill="none" />
                  <path fill="currentColor" fillRule="evenodd" d="M10 19.438L8.955 20.5l-7.666-7.79a1.02 1.02 0 0 1 0-1.42L8.955 3.5L10 4.563L2.682 12z" />
                </svg>
              </button>
              <span className="ttrq-page-title">Private Requests</span>
            </div>
            <p className="ttrq-sub-header">Accept or reject private tutoring requests from students outside your tutoring groups and manage them.</p>
          </div>
        </div>

      
        <div className="ttrq-cards-scroll-area">
          {!hasRequests ? (
            <div className="ttrq-empty-state-container">
              <p className="ttrq-empty-state-text">No requests yet</p>
            </div>
          ) : (
            <>
              {pendingRequests.map((req) => (
                <div key={req._id} className="ttrq-req1">
                  <div className="ttrq-req-name">{req.tuteeName}</div>
                  <div className="ttrq-req-msg">{req.helpText}</div>
                  {req.budget && (
                    <div className="ttrq-req-budget">
                      <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                      </svg>
                      <span>Budget: {req.budget}/week</span>
                    </div>
                  )}
                  <div className="ttrq-req-duration">
                    <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
                      <path d="M0 0h24v24H0z" fill="none" />
                      <g fill="none" stroke="currentColor" strokeLinejoin="round" strokeWidth="1.5">
                        <path strokeLinecap="round" d="m4.095 3.298l15.81-.048m-15.81 17.5l15.81-.048" />
                        <path d="M18.426 3.31H5.574l.079 1.449a7.38 7.38 0 0 0 2.251 4.913l1.242 1.195a1.58 1.58 0 0 1 0 2.279L7.904 14.34a7.38 7.38 0 0 0-2.251 4.913l-.08 1.448h12.853l-.079-1.445a7.38 7.38 0 0 0-2.256-4.917l-1.242-1.194a1.58 1.58 0 0 1 0-2.28l1.242-1.193a7.38 7.38 0 0 0 2.256-4.918z" />
                      </g>
                    </svg>
                    <span className="ttrq-req-duration-text">Duration: {formatDateRange(req.dateRange?.startDate, req.dateRange?.endDate)}</span>
                  </div>
                  <div className="ttrq-req-btns">
                    <button className="ttrq-req-decl" onClick={() => handleDeclineClick(req)}>Decline</button>
                    <button className="ttrq-req-acc" onClick={() => handleAcceptClick(req)}>Accept &amp; Set Rate</button>
                  </div>
                </div>
              ))}

              {acceptedRequests.map((req) => (
                <div key={req._id} className="ttrq-req-accepted">
                  <div className="ttrq-req-name-row">
                    <span className="ttrq-req-name">{req.counterpartyName}</span>
                    <span className="ttrq-accepted-badge">Accepted</span>
                  </div>
                  <div className="ttrq-req-subject">{req.subjectDisplayName}</div>
                  <div className="ttrq-req-duration">
                    <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
                      <path d="M0 0h24v24H0z" fill="none" />
                      <g fill="none" stroke="currentColor" strokeLinejoin="round" strokeWidth="1.5">
                        <path strokeLinecap="round" d="m4.095 3.298l15.81-.048m-15.81 17.5l15.81-.048" />
                        <path d="M18.426 3.31H5.574l.079 1.449a7.38 7.38 0 0 0 2.251 4.913l1.242 1.195a1.58 1.58 0 0 1 0 2.279L7.904 14.34a7.38 7.38 0 0 0-2.251 4.913l-.08 1.448h12.853l-.079-1.445a7.38 7.38 0 0 0-2.256-4.917l-1.242-1.194a1.58 1.58 0 0 1 0-2.28l1.242-1.193a7.38 7.38 0 0 0 2.256-4.918z" />
                      </g>
                    </svg>
                    <span className="ttrq-req-duration-text">Accepted on {formatDate(req.respondedAt)}</span>
                  </div>
                  <div className="ttrq-req-btns">
                    <span className="ttrq-status-in-progress">In Progress</span>
                    <button className="ttrq-req-open-chat" onClick={() => handleOpenChat(req)}>Open Chat</button>
                  </div>
                </div>
              ))}

              {finishedRequests.map((req) => (
                <div key={req._id} className="ttrq-req-accepted">
                  <div className="ttrq-req-name-row">
                    <span className="ttrq-req-name">{req.counterpartyName}</span>
                    <span className="ttrq-finished-badge">Finished</span>
                  </div>
                  <div className="ttrq-req-subject">{req.subjectDisplayName}</div>
                  <div className="ttrq-req-duration">
                    <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
                      <path d="M0 0h24v24H0z" fill="none" />
                      <g fill="none" stroke="currentColor" strokeLinejoin="round" strokeWidth="1.5">
                        <path strokeLinecap="round" d="m4.095 3.298l15.81-.048m-15.81 17.5l15.81-.048" />
                        <path d="M18.426 3.31H5.574l.079 1.449a7.38 7.38 0 0 0 2.251 4.913l1.242 1.195a1.58 1.58 0 0 1 0 2.279L7.904 14.34a7.38 7.38 0 0 0-2.251 4.913l-.08 1.448h12.853l-.079-1.445a7.38 7.38 0 0 0-2.256-4.917l-1.242-1.194a1.58 1.58 0 0 1 0-2.28l1.242-1.193a7.38 7.38 0 0 0 2.256-4.918z" />
                      </g>
                    </svg>
                    <span className="ttrq-req-duration-text">Finished on {formatDate(req.finishedAt)}</span>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        <div className="ttrq-info-bar">
          <div className="ttrq-info-bar-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
              <path d="M0 0h24v24H0z" fill="none" />
              <path fill="#f3f800" d="M12 2.5c-3.81 0-6.5 2.743-6.5 6.119c0 1.536.632 2.572 1.425 3.56c.172.215.347.422.527.635l.096.112c.21.25.427.508.63.774c.404.531.783 1.128.995 1.834a.75.75 0 0 1-1.436.432c-.138-.46-.397-.89-.753-1.357a18 18 0 0 0-.582-.714l-.092-.11c-.18-.212-.37-.436-.555-.667C4.87 12.016 4 10.651 4 8.618C4 4.363 7.415 1 12 1s8 3.362 8 7.619c0 2.032-.87 3.397-1.755 4.5c-.185.23-.375.454-.555.667l-.092.109c-.21.248-.405.481-.582.714c-.356.467-.615.898-.753 1.357a.751.751 0 0 1-1.437-.432c.213-.706.592-1.303.997-1.834c.202-.266.419-.524.63-.774l.095-.112c.18-.213.355-.42.527-.634c.793-.99 1.425-2.025 1.425-3.561C18.5 5.243 15.81 2.5 12 2.5M8.75 18h6.5a.75.75 0 0 1 0 1.5h-6.5a.75.75 0 0 1 0-1.5m.75 3.75a.75.75 0 0 1 .75-.75h3.5a.75.75 0 0 1 0 1.5h-3.5a.75.75 0 0 1-.75-.75" />
            </svg>
          </div>
          <p className="ttrq-info-bar-text">
            Private requests are complimentary for your tutoring role. They allow you to teach outside your 3 subject groups, offering one-on-one tutoring for students who couldn't join any of your subjects because they need a subject you don't offer. These students have screened your profile and believe you could assist them. <strong>For more information, visit support.</strong>
          </p>
        </div>
      </div>

      {/* ACCEPT MODAL */}
      {acceptModalOpen && (
        <div className="ttrq-modal-backdrop ttrq-open">
          <div className="ttrq-modal">
            <button className="ttrq-modal-close" onClick={() => setAcceptModalOpen(false)}>✕</button>
            <div className="ttrq-modal-icon-ring ttrq-green">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
            <div className="ttrq-modal-title">Accept tutoring request</div>
            <div className="ttrq-modal-sub">Create a private group for this student. They'll be added and can start sessions.</div>
            <div className="ttrq-preview-chip">
              <div className="ttrq-preview-info">
                <span className="ttrq-preview-name">{currentRequest?.tuteeName}</span>
                <span className="ttrq-preview-sub">
                  {currentRequest?.subjectDisplayName}
                  {currentRequest?.budget && ` · Budget: ${currentRequest.budget}/week`}
                </span>
              </div>
            </div>
            <div className="ttrq-modal-field">
              <div className="ttrq-modal-label-row">
                <label className="ttrq-modal-label">Group session name</label>
                <span className={`ttrq-modal-char-count ${groupName.length >= 50 ? 'ttrq-warn' : ''}`}>{groupName.length} / 60</span>
              </div>
              <input type="text" className="ttrq-modal-input" maxLength="60" placeholder="e.g., Statistics 101 with Austin" value={groupName} onChange={(e) => setGroupName(e.target.value)} />
            </div>
            <div className="ttrq-intel-message">
              <strong>Tip:</strong> The group name should relate to the subject that will be covered, e.g., <strong>"Programming with C - First Year"</strong>
            </div>
            <div className="ttrq-modal-field">
              <label className="ttrq-modal-label">
                Rate per week (R)
                {currentRequest?.budget && <span style={{ fontWeight: 400, color: '#9090a8', marginLeft: '0.4rem' }}>— Student's budget: {currentRequest.budget}/week</span>}
              </label>
              <div className="ttrq-rate-input-wrap">
                <span className="ttrq-rate-prefix">R</span>
                <input type="number" className="ttrq-modal-input" placeholder="e.g. 280" min="0" step="0.01" value={rate} onChange={(e) => setRate(e.target.value)} />
              </div>
            </div>
            <div className="ttrq-modal-actions">
              <button className="ttrq-btn-cancel" onClick={() => setAcceptModalOpen(false)}>Cancel</button>
              <button className="ttrq-btn-confirm-green" onClick={confirmAccept}>Accept &amp; Create Group</button>
            </div>
          </div>
        </div>
      )}

      {/* DECLINE MODAL */}
      {declineModalOpen && (
        <div className="ttrq-modal-backdrop ttrq-open">
          <div className="ttrq-modal">
            <button className="ttrq-modal-close" onClick={() => setDeclineModalOpen(false)}>✕</button>
            <div className="ttrq-modal-icon-ring ttrq-red">
              <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
                <path d="M0 0h24v24H0z" fill="none" />
                <path fill="currentColor" d="m12 13.4l2.9 2.9q.275.275.7.275t.7-.275t.275-.7t-.275-.7L13.4 12l2.9-2.9q.275-.275.275-.7t-.275-.7t-.7-.275t-.7.275L12 10.6L9.1 7.7q-.275-.275-.7-.275t-.7.275t-.275.7t.275.7l2.9 2.9l-2.9 2.9q-.275.275-.275.7t.275.7t.7.275t.7-.275zm0 8.6q-2.075 0-3.9-.788t-3.175-2.137T2.788 15.9T2 12t.788-3.9t2.137-3.175T8.1 2.788T12 2t3.9.788t3.175 2.137T21.213 8.1T22 12t-.788 3.9t-2.137 3.175t-3.175 2.138T12 22m0-2q3.35 0 5.675-2.325T20 12t-2.325-5.675T12 4T6.325 6.325T4 12t2.325 5.675T12 20m0-8" />
              </svg>
            </div>
            <div className="ttrq-modal-title">Decline tutoring request</div>
            <div className="ttrq-modal-sub">You are about to decline the request from <strong>{currentRequest?.tuteeName}</strong>.</div>
            <div className="ttrq-warning-message">This action will permanently reject this tutoring request. The student will be notified that you cannot assist them at this time. This cannot be undone — if you change your mind, the student would need to send a new request.</div>
            <div className="ttrq-modal-field">
              <label className="ttrq-modal-label">Reason (optional)</label>
              <textarea className="ttrq-modal-textarea" placeholder="e.g., Currently at capacity for this subject" value={declineReason} onChange={(e) => setDeclineReason(e.target.value)} maxLength="150" />
            </div>
            <div className="ttrq-modal-actions">
              <button className="ttrq-btn-cancel" onClick={() => setDeclineModalOpen(false)}>Cancel</button>
              <button className="ttrq-btn-confirm-red" onClick={confirmDecline}>Yes, decline request</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TutorPrivateRequests;